import React, {
  useRef,
  useState
} from 'react';
import {
  Animated,
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View
} from 'react-native';

import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import Button from '../../common/components/Button';
import common_styles, {
  borderRadius,
  margin,
  padding,
  roundedBorder,
  text_styles
} from '../../common/components/CommonStyles';
import {
  APP_TABS,
  COLORS,
  HOUSEHOLD_SCREENS,
  USER_SCREENS
} from '../../common/constants/';
import { MEDIA_PREFIX } from '../../common/constants/urls';

import {
  Title,
  Body1,
  RoundButton,
  SmallUppercase,
  useAppWidth
} from '../../utils/';
import { useTheme } from '../../utils/Theme';
import Dot from '../../assets/Dot';
import User from '../../assets/User';
import { ArrowDown, ArrowRight } from '../../assets/Arrow';

const HouseholdNavItem = ({
index,
onPress,
hasUpdates,
displayName,
setViewTranslatePoints,
setTabWidths,
setTabHeight,
...props}) => {
  const theme = useTheme();

  const handleViewLayout = (event: LayoutChangeEvent) => {
    const { x, width, height } = event.nativeEvent.layout;

    setViewTranslatePoints(viewTranslatePoints => {
      const currentPoints = [...viewTranslatePoints];
      currentPoints[index] = x;
      return currentPoints;
    });
    setTabWidths(tabWidths => {
      const currentTabWidths = [...tabWidths];
      currentTabWidths[index] = width;
      return currentTabWidths;
    });
    setTabHeight(height);
  };

  return (
    <TouchableOpacity
      style={styles.householdNavItem}
      onPress={onPress}
      onLayout={handleViewLayout}
      {...props}
    >
      <View style={styles.householdNavItemDotContainer}>
        <View style={[styles.householdNavItemDot, {opacity: hasUpdates ? 1.0 : 0.0}]}>
          <Dot color={COLORS.RED}/>
        </View>
      </View>
      <Body1 style={theme.general}>
        { displayName }
      </Body1>
      <View style={styles.householdNavItemDotContainer}>
        <View style={styles.householdNavItemDot}/>
      </View>
    </TouchableOpacity>
  );
}


const HouseholdNav = ({
  screens,
  scrollX,
  active_household,
  profile,
  ...props
}) => {
const [viewTranslatePoints, setViewTranslatePoints] = useState([...new Array(screens.length).fill(-1)]);
const [tabWidths, setTabWidths] = useState([...new Array(screens.length).fill(0)]);
const [tabHeight, setTabHeight] = useState(0);

  const theme = useTheme();

  const navigation = useNavigation();

  const { t } = useTranslation('common');

  const app_width = useAppWidth();
  const animatedCurrent = useRef(Animated.divide(scrollX, app_width)).current;

  const inputRange = screens.map((_, i) => i);
  const width = animatedCurrent.interpolate({
    inputRange,
    outputRange: tabWidths,
    extrapolate: 'clamp'
  });
  const translateX = animatedCurrent.interpolate({
    inputRange,
    outputRange: viewTranslatePoints,
    extrapolate: 'clamp'
  });

  const allMeasured = tabWidths.every(w => w > 0) && viewTranslatePoints.every(x => x >= 0);

  let image = '';
  let image_placeholder_uri = '';

  if (profile.userprofile.image && profile.userprofile.image.image) {
    image = profile.userprofile.image.image_50;
    const { image_placeholder } = profile.userprofile.image;
    image_placeholder_uri = {uri: `data:image/png;base64,${image_placeholder}`};
  }

  return (
    <View
      {...props}
      style={[theme.general, props.style]}
    >
      <View
        style={styles.header}
      >
      <View style={styles.headerRow}>
        <TouchableOpacity
          style={styles.householdName}
          onPress={() => navigation.navigate(APP_TABS.household.name, { screen: HOUSEHOLD_SCREENS.householdSwitcher.name })}
        >
          <Title style={theme.general}>
            { (active_household && active_household.name) ? active_household.name : t('householdNav.blankHouseholdNamePlaceholder') }
          </Title>

          <View style={{width: 20, height: 20}}>
            <ArrowDown color={theme.general.color}/>
          </View>
        </TouchableOpacity>
        {
          image ? (
            <TouchableOpacity
              onPress={() => navigation.navigate(APP_TABS.profile.name, {screen: USER_SCREENS.account.name})}
            >
              <Image
                style={common_styles.roundButton}
                defaultSource={image_placeholder_uri}
                source={{uri: `${MEDIA_PREFIX}${image}`}}
              />
            </TouchableOpacity>
          ) : (
            <RoundButton
              style={theme.secondary}
              onPress={() => navigation.navigate(APP_TABS.profile.name, { screen: USER_SCREENS.account.name})}
            >
              <User color={theme.general.color}/>
            </RoundButton>
          )
        }
        </View>
        <View style={styles.headerRow}>
          <View style={[
            theme.secondary,
            styles.householdNav,
          ]}>
            {
              allMeasured ? (
                <Animated.View
                  style={[
                    theme.general,
                    styles.householdNavItem,
                    {
                      position: 'absolute',
                      width,
                      height: tabHeight,
                      transform: [{ translateX }]
                    }
                  ]}
                />
              ) : null
            }
            <View style={styles.householdNav}>
              {
                screens.map(({displayName, hasUpdates, onPress}, index) => (
                  <HouseholdNavItem
                    key={index}
                    index={index}
                    displayName={displayName}
                    hasUpdates={hasUpdates}
                    onPress={onPress}
                    setViewTranslatePoints={setViewTranslatePoints}
                    setTabHeight={setTabHeight}
                    setTabWidths={setTabWidths}
                  />
                ))
              }
            </View>
          </View>
        </View>
      </View>
      <View style={{width: '100%', height: StyleSheet.hairlineWidth, backgroundColor: COLORS.LIGHT_GRAY}}/>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    width: '100%',
    paddingBottom: 0.5 * padding
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: padding,
    marginVertical: margin
  },
  householdName: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  householdNav: {
    borderRadius: 1.5 * borderRadius,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexGrow: 1
  },
  householdNavItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: 0.5 * padding,
    borderRadius: 1.5 * borderRadius
  },
  householdNavItemDotContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: padding
  },
  householdNavItemDot: {width: 5, height: 5},
  householdNavItemText: {
    flex: 1
  },
  householdNavItemCount: {
    flexDirection: 'row',
    alignItems: 'center'
  }
})

export default HouseholdNav;
